import type { GenerateOptions, GenerationEngine, GenerationRequest, GenerationResult, QualityMetrics } from './types';

// Generates a random ID
function generateId(): string {
  return `gen_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Style vibes for AI stylist
const STYLE_VIBES = [
  'Effortlessly Chic',
  'Street Smart',
  'Polished Professional',
  'Bohemian Dream',
  'Modern Minimalist',
  'Retro Revival',
  'Glamour Queen',
  'Casual Cool',
  'Urban Explorer',
  'Classic Elegance',
];

// Occasion suggestions
const OCCASIONS = [
  ['casual', 'brunch', 'shopping'],
  ['work', 'business casual', 'meetings'],
  ['evening', 'dinner', 'date night'],
  ['party', 'nightclub', 'celebration'],
  ['weekend', 'travel', 'sightseeing'],
];

// Look titles for social media
const LOOK_TITLES = ['Urban Explorer', 'City Chic', 'Street Style Star', 'Effortless Elegance', 'Power Move', 'Weekend Vibes', 'Modern Classic', 'Bold & Beautiful', 'Runway Ready', 'Style Statement'];

// Mood descriptors
const MOODS = ['confident', 'playful', 'sophisticated', 'edgy', 'romantic', 'bold', 'relaxed', 'powerful', 'dreamy', 'fierce'];

// Sample hashtags by category
const HASHTAGS = {
  style: ['#OOTD', '#StyleInspo', '#FashionDaily', '#LookOfTheDay', '#StyleDiary'],
  mood: ['#Vibes', '#Mood', '#Aesthetic', '#Slay', '#Iconic'],
  fashion: ['#Fashion', '#FashionForward', '#Trendy', '#ChicStyle', '#FashionLover'],
};

// Sample color palettes
const COLOR_PALETTES = [
  ['#2C3E50', '#E74C3C', '#ECF0F1'], // Navy, Red, White
  ['#1A1A2E', '#16213E', '#E94560'], // Dark Blue, Deep Blue, Pink
  ['#F5F5DC', '#D4A373', '#6B4423'], // Beige, Camel, Brown
  ['#000000', '#FFFFFF', '#808080'], // Black, White, Gray
  ['#2E8B57', '#3CB371', '#F0FFF0'], // Forest, Sea Green, Honeydew
  ['#4A0E4E', '#81267F', '#C154C1'], // Deep Purple, Purple, Orchid
];

// Sample stylist suggestions
const SUGGESTION_TEMPLATES = [
  {
    type: 'accessory' as const,
    priority: 'high' as const,
    title: 'Add a Statement Bag',
    description: 'A structured handbag would elevate this look and add visual interest',
    actionLabel: 'Browse Bags',
  },
  {
    type: 'accessory' as const,
    priority: 'medium' as const,
    title: 'Layer with Jewelry',
    description: 'Delicate gold necklaces or bold statement earrings would complement this style',
    actionLabel: 'Add Jewelry',
  },
  {
    type: 'color' as const,
    priority: 'medium' as const,
    title: 'Pop of Color',
    description: 'Consider adding a bright accent color through accessories or a scarf',
    actionLabel: 'Explore Colors',
  },
  {
    type: 'proportion' as const,
    priority: 'low' as const,
    title: 'Balance the Silhouette',
    description: 'Tucking in the top or adding a belt could create a more defined waistline',
    actionLabel: 'Try Belt',
  },
  {
    type: 'styling' as const,
    priority: 'high' as const,
    title: 'Roll Up Sleeves',
    description: 'Rolling the sleeves would add a relaxed, effortless feel to the look',
  },
  {
    type: 'alternative' as const,
    priority: 'low' as const,
    title: 'Try Different Footwear',
    description: 'Swapping to heeled boots would transform this from casual to evening-ready',
    actionLabel: 'Browse Shoes',
  },
  {
    type: 'accessory' as const,
    priority: 'medium' as const,
    title: 'Sunglasses for Polish',
    description: 'Oversized sunglasses would add an instant cool factor to this ensemble',
    actionLabel: 'Add Sunglasses',
  },
  {
    type: 'styling' as const,
    priority: 'medium' as const,
    title: 'Add a Third Piece',
    description: 'A blazer or cardigan would complete the outfit and add versatility',
    actionLabel: 'Add Layer',
  },
];

// Generate social media captions
function generateCaption(vibe: string, mood: string): string {
  const captions = [
    `Channeling ${mood} energy today. When your outfit matches your attitude. ✨`,
    `${vibe} is not just a look, it's a lifestyle. Dress for the life you want.`,
    `Less is more, but make it memorable. ${vibe} done right.`,
    `Confidence is the best accessory. Feeling ${mood} in this fit.`,
    `Style is a way to say who you are without speaking. Today's mood: ${mood}.`,
  ];
  return captions[Math.floor(Math.random() * captions.length)];
}

// Pick random items from array
function pickRandom<T>(arr: T[], count: number): T[] {
  const shuffled = [...arr].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

// Simulates realistic quality metrics with fashion analysis
function generateMockQuality(): QualityMetrics {
  const vibe = STYLE_VIBES[Math.floor(Math.random() * STYLE_VIBES.length)];
  const mood = MOODS[Math.floor(Math.random() * MOODS.length)];
  const title = LOOK_TITLES[Math.floor(Math.random() * LOOK_TITLES.length)];
  const palette = COLOR_PALETTES[Math.floor(Math.random() * COLOR_PALETTES.length)];
  const occasionSet = OCCASIONS[Math.floor(Math.random() * OCCASIONS.length)];

  // Pick 2-4 random suggestions
  const suggestions = pickRandom(SUGGESTION_TEMPLATES, 2 + Math.floor(Math.random() * 3));

  // Generate hashtags
  const hashtags = [...pickRandom(HASHTAGS.style, 2), ...pickRandom(HASHTAGS.mood, 1), ...pickRandom(HASHTAGS.fashion, 2)];

  return {
    // Technical metrics
    edgeFidelity: Math.floor(Math.random() * 15) + 85, // 85-100
    shadowMapping: Math.random() > 0.2 ? 'good' : 'fair',
    seamDetection: Math.random() > 0.3 ? 'clean' : 'visible',

    // Fashion metrics
    fashion: {
      colorHarmony: Math.floor(Math.random() * 20) + 75, // 75-95
      proportionBalance: Math.floor(Math.random() * 25) + 70, // 70-95
      styleCohesion: Math.floor(Math.random() * 15) + 80, // 80-95
      silhouetteScore: Math.floor(Math.random() * 20) + 75, // 75-95
      trendAlignment: (['current', 'classic', 'retro', 'avant-garde'] as const)[Math.floor(Math.random() * 4)],
      outfitCompleteness: (['complete', 'needs-accessory', 'needs-layer', 'minimal'] as const)[Math.floor(Math.random() * 4)],
    },

    // AI Stylist suggestions
    stylist: {
      overallRating: 3 + Math.floor(Math.random() * 2) + (Math.random() > 0.5 ? 0.5 : 0), // 3-5 stars
      vibe,
      suggestions,
      colorPalette: palette,
      occasion: occasionSet,
    },

    // Look description for social media
    lookDescription: {
      title,
      caption: generateCaption(vibe, mood),
      hashtags,
      mood,
    },
  };
}

// Placeholder image URLs (colored rectangles as data URIs)
const PLACEHOLDER_COLORS = [
  '#7c3aed', // violet
  '#2563eb', // blue
  '#059669', // emerald
  '#d97706', // amber
  '#dc2626', // red
];

function getPlaceholderImageUrl(index = 0): string {
  const color = PLACEHOLDER_COLORS[index % PLACEHOLDER_COLORS.length];
  // Return a simple colored placeholder (in real implementation, this would be AI-generated)
  return `data:image/svg+xml,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="400" height="600" viewBox="0 0 400 600">
      <rect fill="${color}" width="400" height="600"/>
      <text x="200" y="280" fill="white" font-family="Arial" font-size="20" text-anchor="middle">
        Generated Look
      </text>
      <text x="200" y="320" fill="white" font-family="Arial" font-size="14" text-anchor="middle" opacity="0.8">
        Preview ${index + 1}
      </text>
    </svg>
  `)}`;
}

// Simulates generation delay
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * MockEngine - A mock implementation of GenerationEngine for UI development
 * Replace with GeminiNanoEngine for production
 */
export class MockEngine implements GenerationEngine {
  private simulatedDelayMs: number;

  constructor(simulatedDelayMs = 1500) {
    this.simulatedDelayMs = simulatedDelayMs;
  }

  async isAvailable(): Promise<boolean> {
    // Mock engine is always available
    return true;
  }

  async generate(request: GenerationRequest, options?: GenerateOptions): Promise<GenerationResult> {
    options?.onSubmitted?.(`mock-${Date.now()}`);
    // Simulate processing time
    await delay(this.simulatedDelayMs);

    const result: GenerationResult = {
      id: generateId(),
      imageUrl: getPlaceholderImageUrl(0),
      driveUid: null,
      quality: generateMockQuality(),
      timestamp: Date.now(),
      request,
    };

    return result;
  }

  async resumeExecution(_executionId: string, request: GenerationRequest): Promise<GenerationResult> {
    await delay(this.simulatedDelayMs);
    return {
      id: generateId(),
      imageUrl: getPlaceholderImageUrl(0),
      driveUid: null,
      quality: generateMockQuality(),
      timestamp: Date.now(),
      request,
    };
  }

  async generateVariants(base: GenerationResult, count: number): Promise<GenerationResult[]> {
    // Simulate processing time (slightly faster for variants)
    await delay(this.simulatedDelayMs * 0.8);

    const variants: GenerationResult[] = [];

    for (let i = 0; i < count; i++) {
      variants.push({
        id: generateId(),
        imageUrl: getPlaceholderImageUrl(i + 1),
        driveUid: null,
        quality: generateMockQuality(),
        timestamp: Date.now(),
        request: base.request,
      });
    }

    return variants;
  }
}

// Export a singleton instance for convenience
export const mockEngine = new MockEngine();
